import type {
  BaseRendererOptions,
  CurveDef,
  Engine,
  MorphOptions,
  Point,
  RuntimeRenderOptions,
  SarmalInstance,
  TrailColor,
  TrailStyle,
} from "./types";

import {
  DEFAULT_MORPH_DURATION_MS,
  DEFAULT_SKELETON_OPACITY,
  DESTROYED_ERROR,
  TRAIL_MIN_WIDTH,
  TRAIL_MAX_WIDTH,
  easeInOutCubic,
  computeBoundaries,
  computeTrailQuad,
  enginePassthroughs,
  colorToRgb,
  getPaletteColor,
  parseColorToOklab,
  resolveHeadColor,
  resolveTrailPalette,
  resolveTrailMainColor,
  oklabToRgb,
  Oklab,
  validateRenderOptions,
  warnIfTrailColorMismatch,
} from "./renderer-shared";
import { createEngine } from "./engine";

export interface SVGRendererOptions extends BaseRendererOptions {
  /** SVG element the renderer draws into directly */
  container: SVGSVGElement;
  engine: Engine;
  /** @default 'Loading' */
  ariaLabel?: string;
}

export interface SVGSarmalOptions extends Omit<SVGRendererOptions, "container" | "engine"> {
  /** @default 120 */
  trailLength?: number;
}
const EMPTY_PARAMS: Record<string, number> = {};
const SVG_DEFAULT_HEAD_RADIUS = 0.5; // viewBox units
/** Pixel-space skeleton stroke width calibrated to viewBox units at construction like TRAIL_MIN/MAX_WIDTH. */
const SKELETON_STROKE_WIDTH_PX = 1.5;

/**
 * Threshold above which a console.warn suggests canvas for long SVG trails
 * The value is based on results from `playwright test e2e/tests/svg-trail-benchmark.spec.ts --project=chromium-dpr1 --reporter=list`
 */
const HIGH_TRAIL_LENGTH_THRESHOLD = 5000;

function pointsToPathString(pts: Point[], scale: number, offsetX: number, offsetY: number): string {
  if (pts.length < 2) {
    return "";
  }

  const px = (p: Point) => (p.x * scale + offsetX).toFixed(2);
  const py = (p: Point) => (p.y * scale + offsetY).toFixed(2);
  let d = `M${px(pts[0]!)} ${py(pts[0]!)}`;

  for (let i = 1; i < pts.length; i++) {
    d += ` L${px(pts[i]!)} ${py(pts[i]!)}`;
  }

  return d + " Z";
}

function sampleCurveSkeleton(curveDef: CurveDef): Point[] {
  const period = curveDef.period ?? Math.PI * 2;
  const samples = Math.ceil(period * 50); // match engine's POINTS_PER_PERIOD_UNIT
  const pts: Point[] = Array.from({ length: samples });

  for (let i = 0; i < samples; i++) {
    const phase = (i / (samples - 1)) * period;
    pts[i] = curveDef.skeletonFn ? curveDef.skeletonFn(phase) : curveDef.fn(phase, 0, EMPTY_PARAMS);
  }

  return pts;
}

function el(tag: string): SVGElement {
  return document.createElementNS("http://www.w3.org/2000/svg", tag);
}

function colorToRgbAttr(color: string): string {
  const { r, g, b } = colorToRgb(color);
  return `rgb(${r},${g},${b})`;
}

/**
 * Creates a live SVG renderer for sarmal animations
 * The SVG is appended into `container` and updated each frame via **requestAnimationFrame**
 */
export function createSVGRenderer(options: SVGRendererOptions): SarmalInstance {
  const { container, engine } = options;
  const poolSize = engine.trailLength;

  if (poolSize > HIGH_TRAIL_LENGTH_THRESHOLD) {
    console.warn(
      `[sarmal] High trailLength in SVG renderer (${poolSize}). Consider using the canvas renderer for long trails.`,
    );
  }

  // TODO: duplicate let variables. maybe a better way to share these too?
  // ! Mutated only by `setRenderOptions`.
  let trailStyle: TrailStyle = options.trailStyle ?? "default";
  let trailColor: TrailColor = options.trailColor ?? "#ffffff";
  let skeletonColor: string = options.skeletonColor ?? "#ffffff";
  // `null` means that head color is derived from `trailColor` & `trailStyle` and refreshed whenever either changes.
  let userHeadColor: string | null = options.headColor ?? null;
  let headColor: string = userHeadColor ?? resolveHeadColor(trailColor, trailStyle);
  let headRadius: number; // assigned below after container size is read

  let trailSolid: string = colorToRgbAttr(resolveTrailMainColor(trailColor));
  let trailPalette: string[] = resolveTrailPalette(trailColor);
  let trailPaletteOklab: Oklab[] = trailPalette.map((c) => parseColorToOklab(c)!);

  const ariaLabel = options.ariaLabel ?? "Loading";

  warnIfTrailColorMismatch(trailColor, trailStyle);

  const viewSize = 100;

  function getContainerPixelSize(): number {
    const rect = container.getBoundingClientRect();
    return rect.width && rect.height ? Math.min(rect.width, rect.height) : 200;
  }

  /**
   * Trail widths and skeleton stroke are calibrated from pixel-space shared constants so that
   *  strokes render at a consistent physical thickness regardless of container dimensions.
   * ! headRadius is NOT calibrated. It is native viewBox units (see SVG_DEFAULT_HEAD_RADIUS).
   */
  const containerPx = getContainerPixelSize();
  const svgTrailMinWidth = (TRAIL_MIN_WIDTH * viewSize) / containerPx;
  const svgTrailMaxWidth = (TRAIL_MAX_WIDTH * viewSize) / containerPx;
  const svgSkeletonStrokeWidth = String((SKELETON_STROKE_WIDTH_PX * viewSize) / containerPx);

  if (options.headRadius !== undefined) {
    validateRenderOptions({ headRadius: options.headRadius });
  }

  if (options.trailWidth !== undefined) {
    validateRenderOptions({ trailWidth: options.trailWidth });
  }
  headRadius = options.headRadius ?? SVG_DEFAULT_HEAD_RADIUS;
  let trailWidth = options.trailWidth ?? 1;

  container.setAttribute("viewBox", `0 0 ${viewSize} ${viewSize}`);
  container.setAttribute("role", "img");
  container.setAttribute("aria-label", ariaLabel);

  const group = el("g") as SVGGElement;

  const titleEl = el("title");
  titleEl.textContent = ariaLabel;
  group.appendChild(titleEl);

  const skeletonPath = el("path") as SVGPathElement;
  skeletonPath.setAttribute("data-sarmal-role", "skeleton");
  skeletonPath.setAttribute("fill", "none");
  skeletonPath.setAttribute(
    "stroke",
    skeletonColor === "transparent" ? "transparent" : colorToRgbAttr(skeletonColor),
  );
  skeletonPath.setAttribute("stroke-opacity", String(DEFAULT_SKELETON_OPACITY));
  skeletonPath.setAttribute("stroke-width", svgSkeletonStrokeWidth);
  if (skeletonColor === "transparent") {
    skeletonPath.setAttribute("visibility", "hidden");
  }
  group.appendChild(skeletonPath);

  const skeletonPathA = el("path") as SVGPathElement;
  skeletonPathA.setAttribute("fill", "none");
  skeletonPathA.setAttribute(
    "stroke",
    skeletonColor === "transparent" ? "transparent" : colorToRgbAttr(skeletonColor),
  );
  skeletonPathA.setAttribute("stroke-width", svgSkeletonStrokeWidth);
  skeletonPathA.setAttribute("visibility", "hidden");
  group.appendChild(skeletonPathA);

  const skeletonPathB = el("path") as SVGPathElement;
  skeletonPathB.setAttribute("fill", "none");
  skeletonPathB.setAttribute(
    "stroke",
    skeletonColor === "transparent" ? "transparent" : colorToRgbAttr(skeletonColor),
  );
  skeletonPathB.setAttribute("stroke-width", svgSkeletonStrokeWidth);
  skeletonPathB.setAttribute("visibility", "hidden");
  group.appendChild(skeletonPathB);

  let morphPathABuilt = "";
  let morphPathBBuilt = "";

  const trailPaths: SVGPathElement[] = [];
  for (let i = 0; i < poolSize; i++) {
    const path = el("path") as SVGPathElement;
    path.setAttribute("fill", trailSolid);
    group.appendChild(path);
    trailPaths.push(path);
  }

  const headCircle = el("circle") as SVGCircleElement;
  headCircle.setAttribute("data-sarmal-role", "head");
  headCircle.setAttribute("fill", colorToRgbAttr(headColor));
  headCircle.setAttribute("r", String(headRadius));
  group.appendChild(headCircle);

  container.appendChild(group);

  let gradientAnimTime = 0;
  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;

  function applyBoundaries(skeleton: Point[]) {
    const b = computeBoundaries(skeleton, viewSize, viewSize);
    if (b) {
      scale = b.scale;
      offsetX = b.offsetX;
      offsetY = b.offsetY;
    }
  }

  // Coordinate transform helpers: numeric (for math)
  function px(p: Point): number {
    return p.x * scale + offsetX;
  }
  function py(p: Point): number {
    return p.y * scale + offsetY;
  }

  function updateSkeleton(skeleton: Point[]) {
    skeletonPath.setAttribute("d", pointsToPathString(skeleton, scale, offsetX, offsetY));
  }

  const skeleton = engine.getSarmalSkeleton();
  applyBoundaries(skeleton);

  if (!engine.isLiveSkeleton) {
    updateSkeleton(skeleton);
  }

  function updateTrail(trail: Point[], trailCount: number) {
    if (trailCount < 2) {
      for (const p of trailPaths) {
        p.setAttribute("d", "");
      }

      return;
    }

    /**
     * Ribbon approach: each segment is a filled quad with its own opacity.
     * Quads are drawn from tail to head, with later quads overlaying earlier ones.
     */
    const drawnCount = trailCount - 1;

    for (let i = 0; i < drawnCount; i++) {
      const { l0x, l0y, r0x, r0y, l1x, l1y, r1x, r1y, opacity, progress } = computeTrailQuad(
        trail,
        i,
        trailCount,
        px,
        py,
        svgTrailMinWidth * trailWidth,
        svgTrailMaxWidth * trailWidth,
      );

      const d = `M${l0x.toFixed(2)} ${l0y.toFixed(2)} L${l1x.toFixed(2)} ${l1y.toFixed(2)} L${r1x.toFixed(2)} ${r1y.toFixed(2)} L${r0x.toFixed(2)} ${r0y.toFixed(2)} Z`;

      trailPaths[i]!.setAttribute("d", d);
      trailPaths[i]!.setAttribute("fill-opacity", opacity.toFixed(3));

      if (trailStyle !== "default") {
        const timeOffset = trailStyle === "gradient-animated" ? gradientAnimTime * 0.0005 : 0;
        const { r, g, b } = oklabToRgb(getPaletteColor(trailPaletteOklab, progress, timeOffset));
        trailPaths[i]!.setAttribute("fill", `rgb(${r},${g},${b})`);
      }
    }

    // Hide unused paths
    for (let i = drawnCount; i < trailPaths.length; i++) {
      trailPaths[i]!.setAttribute("d", "");
    }
  }

  function updateHead(trail: Point[], trailCount: number) {
    if (trailCount === 0) {
      return;
    }

    const head = trail[trailCount - 1]!;
    const x = px(head);
    const y = py(head);

    headCircle.setAttribute("cx", String(x));
    headCircle.setAttribute("cy", String(y));
  }

  let animationId: number | null = null;
  let lastTime = 0;
  let pausedByVisibility = false;
  let destroyed = false;
  const prefersReducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let morphResolve: (() => void) | null = null;
  let morphReject: ((error: Error) => void) | null = null;
  let morphDurationMs = DEFAULT_MORPH_DURATION_MS;
  let morphTarget: CurveDef | null = null;
  // Raw linear progress of the morph over time (0 to 1), used for timing and completion.
  // The eased value derived from it drives both the blend and the skeleton crossfade.
  let morphProgress = 0;
  let morphEasingFn: (t: number) => number = easeInOutCubic;

  function renderFrame(deltaTime: number) {
    if (trailStyle === "gradient-animated") {
      gradientAnimTime += deltaTime * 1000;
    }

    if (engine.morphAlpha !== null) {
      morphProgress = Math.min(1, morphProgress + deltaTime / (morphDurationMs / 1000));
      // Eased progress drives the blend; the skeleton opacity crossfade uses the same
      // eased value so the fading shapes stay in lockstep with the morph.
      const easedAlpha = morphEasingFn(morphProgress);
      engine.setMorphAlpha(easedAlpha);

      if (morphPathABuilt) {
        skeletonPathA.setAttribute("d", morphPathABuilt);
        skeletonPathA.setAttribute("visibility", "visible");
        skeletonPathA.setAttribute(
          "stroke-opacity",
          String((1 - easedAlpha) * DEFAULT_SKELETON_OPACITY),
        );
      }

      if (morphPathBBuilt) {
        skeletonPathB.setAttribute("d", morphPathBBuilt);
        skeletonPathB.setAttribute("visibility", "visible");
        skeletonPathB.setAttribute("stroke-opacity", String(easedAlpha * DEFAULT_SKELETON_OPACITY));
      }

      if (morphProgress >= 1) {
        engine.completeMorph();
        morphResolve?.();
        morphResolve = null;
        morphReject = null;
        morphTarget = null;
        morphProgress = 0;
        morphPathABuilt = "";
        morphPathBBuilt = "";
        skeletonPathA.setAttribute("visibility", "hidden");
        skeletonPathB.setAttribute("visibility", "hidden");
        // Snap coordinate space to `curveB` and update skeleton path
        const newSkeleton = engine.getSarmalSkeleton();
        applyBoundaries(newSkeleton);
        updateSkeleton(newSkeleton);
      }
    }

    const trail = engine.tick(deltaTime);
    const trailCount = engine.trailCount;

    if (engine.isLiveSkeleton && engine.morphAlpha === null) {
      const liveSkeleton = engine.getSarmalSkeleton();
      applyBoundaries(liveSkeleton);
      updateSkeleton(liveSkeleton);
    }

    updateTrail(trail, trailCount);
    updateHead(trail, trailCount);
  }

  function loop() {
    const now = performance.now();
    const deltaTime = Math.min((now - lastTime) / 1000, 1 / 30);
    lastTime = now;

    renderFrame(deltaTime);

    if (!prefersReducedMotion) {
      animationId = requestAnimationFrame(loop);
    }
  }

  // Handle initialPhase option: seek to the specified position before first frame
  if (options.initialPhase !== undefined) {
    engine.seek(options.initialPhase);
  }

  // Draw initial frame unconditionally (shows skeleton and initial position)
  renderFrame(0);

  // Handle autoStart option: start the animation loop unless explicitly disabled
  const shouldAutoStart = options.autoStart !== false;

  const instance = {
    play() {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      if (animationId !== null) {
        return;
      }

      lastTime = performance.now();
      loop();
    },

    pause() {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      if (animationId === null) {
        return;
      }

      cancelAnimationFrame(animationId);
      animationId = null;
      engine.cancelSpeedTransition();
    },

    reset() {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      engine.reset();
    },

    destroy() {
      if (destroyed) {
        return;
      }

      destroyed = true;

      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);

      engine.cancelSpeedTransition();

      if (morphReject !== null) {
        morphReject(new Error("[sarmal] Instance destroyed during morph"));
        morphResolve = null;
        morphReject = null;
      }

      group.remove();
    },

    ...enginePassthroughs(engine, () => destroyed),

    morphTo(target: CurveDef, options?: MorphOptions): Promise<void> {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      if (morphResolve !== null) {
        engine.completeMorph();
        morphResolve();
        morphResolve = null;
        morphReject = null;
        morphProgress = 0;
        skeletonPathA.setAttribute("visibility", "hidden");
        skeletonPathB.setAttribute("visibility", "hidden");
      }

      morphDurationMs = options?.duration ?? DEFAULT_MORPH_DURATION_MS;
      morphEasingFn = options?.easing ?? easeInOutCubic;
      morphTarget = target;
      morphProgress = 0;

      const currentSkeleton = engine.getSarmalSkeleton();
      morphPathABuilt = pointsToPathString(currentSkeleton, scale, offsetX, offsetY);

      engine.startMorph(target, options?.morphStrategy, options?.align ?? false);

      if (morphTarget) {
        const targetSkeleton = sampleCurveSkeleton(target);
        morphPathBBuilt = pointsToPathString(targetSkeleton, scale, offsetX, offsetY);
      }

      return new Promise<void>((resolve, reject) => {
        morphResolve = resolve;
        morphReject = reject;
      });
    },

    setRenderOptions(partial: RuntimeRenderOptions): void {
      if (destroyed) {
        throw new Error(DESTROYED_ERROR);
      }

      validateRenderOptions(partial);

      const prevTrailStyle = trailStyle;

      if (partial.trailColor !== undefined) {
        trailColor = partial.trailColor;
        trailSolid = colorToRgbAttr(resolveTrailMainColor(trailColor));
        trailPalette = resolveTrailPalette(trailColor);
        trailPaletteOklab = trailPalette.map((c) => parseColorToOklab(c)!);

        // Only the "default" style paints trail paths at setter time, whereas gradient styles repaint per-segment fills every frame in `updateTrail`,
        //  so they would already overwrite any fill operation made here
        if (trailStyle === "default") {
          for (const p of trailPaths) {
            p.setAttribute("fill", trailSolid);
          }
        }
      }

      if (partial.skeletonColor !== undefined) {
        skeletonColor = partial.skeletonColor;

        if (skeletonColor === "transparent") {
          skeletonPath.setAttribute("visibility", "hidden");
        } else {
          skeletonPath.setAttribute("stroke", colorToRgbAttr(skeletonColor));
          skeletonPath.removeAttribute("visibility");
          skeletonPathA.setAttribute("stroke", colorToRgbAttr(skeletonColor));
          skeletonPathB.setAttribute("stroke", colorToRgbAttr(skeletonColor));
        }
      }

      if (partial.trailStyle !== undefined) {
        trailStyle = partial.trailStyle;

        // When transitioning from gradient to default, the whole trail needs to be filled with the color
        // Transitioning from default to gradient does not require this as it already does per-segment color fills
        if (prevTrailStyle !== "default" && trailStyle === "default") {
          for (const p of trailPaths) {
            p.setAttribute("fill", trailSolid);
          }
        }
      }
      if (partial.headColor !== undefined) {
        userHeadColor = partial.headColor;
      }

      if (partial.headRadius !== undefined) {
        headRadius = partial.headRadius;
        headCircle.setAttribute("r", String(headRadius));
      }

      if (partial.trailWidth !== undefined) {
        trailWidth = partial.trailWidth;
      }

      if (userHeadColor === null) {
        headColor = resolveHeadColor(trailColor, trailStyle);
      } else {
        headColor = userHeadColor;
      }
      headCircle.setAttribute("fill", colorToRgbAttr(headColor));

      if (partial.trailColor !== undefined || partial.trailStyle !== undefined) {
        warnIfTrailColorMismatch(trailColor, trailStyle);
      }
    },
  };

  const pauseOnHidden = options.pauseOnHidden !== false;

  function handleVisibilityChange() {
    if (document.hidden) {
      if (animationId !== null) {
        instance.pause();
        pausedByVisibility = true;
      }
    } else {
      if (pausedByVisibility) {
        pausedByVisibility = false;
        instance.play();
      }
    }
  }

  if (pauseOnHidden) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  // If the tab is already hidden at construction time, skip auto-start.
  // The visibilitychange listener will resume when the tab becomes visible.
  const actuallyAutoStart = shouldAutoStart && !(pauseOnHidden && document.hidden);
  if (actuallyAutoStart) {
    instance.play();
  } else if (shouldAutoStart) {
    pausedByVisibility = true;
  }

  return instance;
}

/**
 * Creates a sarmal animation directly inside an `<svg>` element using an SVG renderer.
 * The passed `<svg>` element is set to `viewBox="0 0 100 100"` and animated via requestAnimationFrame
 *
 * @example
 * ```ts
 * import { createSarmalSVG, epitrochoid7 } from '@sarmal/core'
 *
 * // <svg id="spinner"></svg> in your HTML
 * const svg = document.getElementById('spinner')
 * const sarmal = createSarmalSVG(svg, epitrochoid7)
 *
 * // To control manually, use autoStart: false
 * const controlled = createSarmalSVG(svg, rose5, { autoStart: false })
 * controlled.play()  // Start when ready
 * controlled.pause() // Pause later
 * ```
 */
export function createSarmalSVG(
  container: SVGSVGElement,
  curveDef: CurveDef,
  options?: SVGSarmalOptions,
): SarmalInstance {
  const { trailLength, ...rendererOpts } = options ?? {};
  const engine = createEngine(curveDef, trailLength);
  return createSVGRenderer({ container, engine, ...rendererOpts });
}
